import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Alert, AlertDescription } from '@/app/components/ui/alert';
import { Separator } from '@/app/components/ui/separator';
import { AlertCircle, Loader2, Building2, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/contexts/auth-context';
import { getAccessibleModules, getBoardLandingRoute, usePermissions } from '@/hooks/use-permissions';
import { usePlatformModules } from '@/hooks/use-modules';
import { apiClient, setAuthFlag, isAuthenticated as checkAuthCookie } from '@/lib/api-client';
import { clearMFAEnrollmentAccount, setMFAEnrollmentAccount } from '@/lib/mfa-enrollment-account';
import type { SAMLCheckResponse } from '@/lib/types';

interface SocialProvider {
  id: string;
  name: string;
  client_id: string;
}

const loginSchema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

// Fixed messages for ?error= codes; text from the URL is never displayed.
const LOGIN_ERROR_MESSAGES: Record<string, string> = {
  ambiguous_email: 'Multiple accounts match this email. Contact support.',
  sso_refused: 'Sign in with your password and two-factor authentication.',
};

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, login, verifyMFA, isAuthenticated, refreshUser } = useAuth();
  const [error, setError] = useState<string | null>(() => {
    const code = searchParams.get('error') ?? '';
    return Object.prototype.hasOwnProperty.call(LOGIN_ERROR_MESSAGES, code)
      ? LOGIN_ERROR_MESSAGES[code]
      : null;
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [socialProviders, setSocialProviders] = useState<SocialProvider[]>([]);
  const [ssoLoading, setSsoLoading] = useState<string | null>(null);

  // MFA challenge state
  const [mfaToken, setMfaToken] = useState<string | null>(() => (
    new URLSearchParams(window.location.hash.slice(1)).get('sso_mfa_token')
  ));
  const [mfaCode, setMfaCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [mfaSubmitting, setMfaSubmitting] = useState(false);

  // SAML SSO state
  const [showSamlPrompt, setShowSamlPrompt] = useState(false);
  const [samlDomain, setSamlDomain] = useState('');
  const [samlChecking, setSamlChecking] = useState(false);

  const skipRedirectForStaffNoOrg = useRef(false);

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? searchParams.get('next') ?? undefined;

  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.slice(1));
    if (params.has('sso_mfa_token')) {
      window.history.replaceState(null, '', '/login');
    }
  }, []);

  // Fetch configured social providers
  useEffect(() => {
    apiClient
      .get<{ providers: SocialProvider[] }>('/api/v1/auth/social/providers/')
      .then((res) => setSocialProviders(res.data.providers))
      .catch(() => {
        // SSO not configured — buttons simply won't appear
      });
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const isStaffWithoutOrg = Boolean(user?.is_staff && !user?.organization);

  // Redirect if already authenticated (must be in useEffect, not render body,
  // to avoid "Cannot update a component while rendering a different component").
  // Org users without a `from` target are redirected by <LandingRedirect />
  // below once the org's enabled modules are known.
  useEffect(() => {
    if (isAuthenticated) {
      // Allow an already-authenticated platform-admin user without an org to
      // stay on /login so they can switch accounts. A fresh login (including
      // MFA) sets this ref so the redirect still goes to the default route.
      if (
        location.pathname === '/login' &&
        !from &&
        isStaffWithoutOrg &&
        !skipRedirectForStaffNoOrg.current
      ) {
        return;
      }
      skipRedirectForStaffNoOrg.current = false;
      if (from) {
        navigate(from, { replace: true });
      } else if (isStaffWithoutOrg) {
        navigate('/platform-admin', { replace: true });
      }
    }
  }, [isAuthenticated, isStaffWithoutOrg, from, navigate, location.pathname]);

  const onSubmit = async (data: LoginFormData) => {
    setError(null);
    setIsSubmitting(true);

    try {
      const result = await login(data.username, data.password);
      // Check if MFA is required
      if (result && 'mfa_required' in result && result.mfa_required) {
        setMfaToken(result.mfa_token);
        setMfaCode('');
        setUseBackupCode(false);
        return;
      }
      // Mark that this was a fresh login so the redirect effect sends a
      // staff-without-org user to /platform-admin instead of leaving them on
      // /login to switch accounts.
      skipRedirectForStaffNoOrg.current = true;
      // Navigation is handled by the useEffect above once isAuthenticated
      // becomes true — this avoids a race between two navigate() calls.
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Invalid credentials. Please try again.';
      if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
        setError('Invalid username or password. Please try again.');
      } else if (errorMessage.includes('Network')) {
        setError('Unable to connect to the server. Please check your connection.');
      } else {
        setError('Invalid credentials. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const onMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaToken || !mfaCode.trim()) return;

    setError(null);
    setMfaSubmitting(true);

    try {
      await verifyMFA(mfaToken, mfaCode.trim(), useBackupCode);
      // Mark that this was a fresh login so the redirect effect sends a
      // staff-without-org user to their default route.
      skipRedirectForStaffNoOrg.current = true;
      // Navigation handled by useEffect when isAuthenticated becomes true
    } catch (err) {
      // AxiosError.message is generic ("Request failed with status code 401").
      // The meaningful text lives in err.response.data.detail.
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? '';
      if (detail.includes('log in again') || detail.includes('expired')) {
        setError('MFA challenge expired. Please log in again.');
        setMfaToken(null);
      } else if (detail.includes('Invalid') || detail.includes('invalid')) {
        setError('Invalid or expired code. Please try again.');
      } else {
        setError('Verification failed. Please try again.');
      }
    } finally {
      setMfaSubmitting(false);
    }
  };

  const handleSocialLogin = async (provider: SocialProvider) => {
    clearMFAEnrollmentAccount();
    setSsoLoading(provider.id);
    setError(null);

    try {
      // The popup opens the allauth OAuth redirect flow.  After the provider
      // callback completes, allauth redirects to the session-bridge endpoint
      // which converts the Django session into JWT httpOnly cookies and then
      // renders a small HTML page that sets the auth-flag cookie, posts a
      // message to the opener, and closes the popup.
      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      // After allauth finishes, redirect the popup to the session→JWT bridge
      const bridgeUrl = `${apiClient.defaults.baseURL}/api/v1/auth/social/callback/`;
      const redirectUrl = `${apiClient.defaults.baseURL}/accounts/${provider.id}/login/?process=login&next=${encodeURIComponent(bridgeUrl)}`;

      const popup = window.open(
        redirectUrl,
        `sso_${provider.id}`,
        `width=${width},height=${height},left=${left},top=${top},popup=yes`
      );

      if (!popup) {
        // Popup blocked — fall back to full redirect
        // eslint-disable-next-line react-hooks/immutability
        window.location.href = redirectUrl;
        return;
      }

      // Listen for the postMessage from the bridge page AND poll for popup close
      const popupMonitor: { timer?: number } = {};
      const stopPopupMonitoring = () => {
        window.removeEventListener('message', onMessage);
        if (popupMonitor.timer !== undefined) {
          window.clearInterval(popupMonitor.timer);
        }
      };
      const onMessage = (event: MessageEvent) => {
        if (event.origin !== new URL(apiClient.defaults.baseURL ?? '').origin) return;
        if (event.data?.type === 'sso_complete') {
          stopPopupMonitoring();
          setAuthFlag();
          if (event.data?.mfa_enrollment_required) {
            window.location.href = '/enroll-mfa';
            return;
          }
          // Mark that this was a fresh login so a staff-without-org user is
          // redirected to their default route by the useEffect.
          skipRedirectForStaffNoOrg.current = true;
          // Navigation is handled by the useEffect once isAuthenticated becomes true.
          refreshUser();
          setSsoLoading(null);
        } else if (event.data?.type === 'sso_mfa_required') {
          stopPopupMonitoring();
          setMfaToken(event.data.mfa_token);
          setMfaCode('');
          setUseBackupCode(false);
          setSsoLoading(null);
        } else if (event.data?.type === 'sso_failed') {
          stopPopupMonitoring();
          setError('Social sign-in failed. Please try again.');
          setSsoLoading(null);
        }
      };
      window.addEventListener('message', onMessage);

      // Fallback: poll until the popup closes in case postMessage is blocked
      popupMonitor.timer = window.setInterval(() => {
        if (popup.closed) {
          stopPopupMonitoring();
          // Check if the bridge set the auth-flag cookie via JWT cookies
          refreshUser().then(() => {
            if (!checkAuthCookie()) {
              setError('Social sign-in was not completed. Please try again.');
            } else {
              // Mark that this was a fresh login so a staff-without-org user is
              // redirected to their default route by the useEffect.
              skipRedirectForStaffNoOrg.current = true;
            }
            // Navigation is handled by the useEffect once isAuthenticated becomes true.
            setSsoLoading(null);
          });
        }
      }, 500);
    } catch {
      setError('Failed to initiate social sign-in. Please try again.');
      setSsoLoading(null);
    }
  };

  const handleSamlSso = async () => {
    if (!samlDomain.trim()) {
      setError('Please enter your company email domain or organization slug.');
      return;
    }
    const samlAccount = samlDomain.trim();
    clearMFAEnrollmentAccount();
    if (samlAccount.includes('@')) {
      setMFAEnrollmentAccount(samlAccount);
    }
    setSamlChecking(true);
    setError(null);
    try {
      const res = await apiClient.get<SAMLCheckResponse>(
        `/api/v1/auth/saml/check/?domain=${encodeURIComponent(samlDomain.trim())}`
      );
      if (res.data.saml_enabled && res.data.login_url) {
        window.location.href = `${apiClient.defaults.baseURL}${res.data.login_url}`;
      } else {
        setError('SSO is not configured for your organization. Please contact your admin.');
      }
    } catch {
      setError('SSO is not configured for your organization. Please contact your admin.');
    } finally {
      setSamlChecking(false);
    }
  };

  // Signed-in org user with no explicit target: land by module access.
  if (isAuthenticated && !from && !isStaffWithoutOrg) {
    return <LandingRedirect />;
  }

  // MFA challenge screen
  if (mfaToken) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 flex flex-col items-center">
          <img src="/logos/h3ight.svg" alt="H3ight" className="h-8 mb-4" />
          <ShieldCheck className="h-10 w-10 text-blue-600 mb-2" />
          <CardTitle className="text-2xl font-bold">Two-Factor Authentication</CardTitle>
          <CardDescription>
            {useBackupCode
              ? 'Enter one of your backup codes'
              : 'Enter the 6-digit code from your authenticator app'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onMfaSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="mfa_code">
                {useBackupCode ? 'Backup code' : 'Authentication code'}
              </Label>
              <Input
                id="mfa_code"
                type="text"
                inputMode={useBackupCode ? 'text' : 'numeric'}
                autoComplete="one-time-code"
                placeholder={useBackupCode ? 'e.g. a1b2c3d4' : '000000'}
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                disabled={mfaSubmitting}
                autoFocus
                maxLength={useBackupCode ? 20 : 6}
              />
            </div>

            <Button type="submit" className="w-full" disabled={mfaSubmitting || !mfaCode.trim()}>
              {mfaSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify'
              )}
            </Button>

            <div className="flex justify-between text-sm">
              <button
                type="button"
                className="text-blue-600 hover:text-blue-500"
                onClick={() => {
                  setUseBackupCode(!useBackupCode);
                  setMfaCode('');
                  setError(null);
                }}
              >
                {useBackupCode ? 'Use authenticator app' : 'Use a backup code'}
              </button>
              <button
                type="button"
                className="text-gray-500 hover:text-gray-700"
                onClick={() => {
                  setMfaToken(null);
                  setMfaCode('');
                  setError(null);
                }}
              >
                Back to login
              </button>
            </div>
          </form>
          <div className="mt-6 flex justify-center">
            <img src="/logos/powered-by-h3.svg" alt="Powered by H3" className="h-9" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="space-y-1 flex flex-col items-center">
        <img src="/logos/h3ight.svg" alt="H3ight" className="h-8 mb-4" />
        <CardTitle className="text-2xl font-bold">Welcome back</CardTitle>
        <CardDescription>
          Sign in to your H3ight account
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Social / SSO buttons */}
        {socialProviders.length > 0 && (
          <div className="space-y-2 mb-4">
            {socialProviders.map((provider) => (
              <Button
                key={provider.id}
                type="button"
                variant="outline"
                className="w-full"
                disabled={ssoLoading !== null || isSubmitting}
                onClick={() => handleSocialLogin(provider)}
              >
                {ssoLoading === provider.id ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <SsoProviderIcon providerId={provider.id} />
                )}
                Sign in with {provider.name}
              </Button>
            ))}
          </div>
        )}

        {/* SAML SSO */}
        {!showSamlPrompt ? (
          <Button
            type="button"
            variant="outline"
            className="w-full mb-4"
            disabled={isSubmitting || ssoLoading !== null}
            onClick={() => setShowSamlPrompt(true)}
          >
            <Building2 className="mr-2 h-4 w-4" />
            Sign in with SSO
          </Button>
        ) : (
          <div className="space-y-2 mb-4">
            <Label htmlFor="saml_domain" className="text-sm">Company email domain or organization slug</Label>
            <div className="flex gap-2">
              <Input
                id="saml_domain"
                placeholder="apexropeaccess.com or apex"
                value={samlDomain}
                onChange={(e) => setSamlDomain(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSamlSso(); } }}
                disabled={samlChecking}
              />
              <Button type="button" onClick={handleSamlSso} disabled={samlChecking}>
                {samlChecking ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Go'}
              </Button>
            </div>
            <button type="button" className="text-xs text-gray-500 hover:text-gray-700" onClick={() => setShowSamlPrompt(false)}>Cancel</button>
          </div>
        )}

        {(socialProviders.length > 0 || true) as boolean && (
          <div className="relative my-4">
            <Separator />
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-2 text-xs text-muted-foreground">
              or continue with credentials
            </span>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              type="text"
              placeholder="john.doe"
              {...register('username')}
              disabled={isSubmitting}
            />
            {errors.username && (
              <p className="text-sm text-red-500">{errors.username.message}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-500">
                Forgot password?
              </Link>
            </div>
            <Input
              id="password"
              type="password"
              {...register('password')}
              disabled={isSubmitting}
            />
            {errors.password && (
              <p className="text-sm text-red-500">{errors.password.message}</p>
            )}
          </div>
          
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Signing in...
              </>
            ) : (
              'Sign in'
            )}
          </Button>
        </form>
        <div className="mt-6 flex justify-center">
          <img src="/logos/powered-by-h3.svg" alt="Powered by H3" className="h-9" />
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Sends a signed-in user to their landing route once the org's enabled modules
 * are known. Only mounted after authentication so the modules query is never
 * fired from the anonymous login screen. If the modules request fails the
 * persona rule alone decides, as before.
 */
function LandingRedirect() {
  const navigate = useNavigate();
  const { boardPersona, getModuleRole } = usePermissions();
  const { enabledModules, isLoading } = usePlatformModules();

  useEffect(() => {
    if (isLoading) return;
    navigate(
      getBoardLandingRoute(boardPersona, getAccessibleModules(enabledModules, getModuleRole)),
      { replace: true },
    );
    // getModuleRole is recreated every render; the inputs that matter are
    // boardPersona and the module list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardPersona, enabledModules.join(','), isLoading, navigate]);

  return null;
}

/** Renders a small provider icon (inline SVG to avoid external deps). */
function SsoProviderIcon({ providerId }: { providerId: string }) {
  if (providerId === 'google') {
    return (
      <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
        <path
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
          fill="#4285F4"
        />
        <path
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          fill="#34A853"
        />
        <path
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          fill="#FBBC05"
        />
        <path
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          fill="#EA4335"
        />
      </svg>
    );
  }

  if (providerId === 'microsoft') {
    return (
      <svg className="mr-2 h-4 w-4" viewBox="0 0 23 23">
        <rect x="1" y="1" width="10" height="10" fill="#f25022" />
        <rect x="12" y="1" width="10" height="10" fill="#7fba00" />
        <rect x="1" y="12" width="10" height="10" fill="#00a4ef" />
        <rect x="12" y="12" width="10" height="10" fill="#ffb900" />
      </svg>
    );
  }

  return null;
}
